// MACHINERY V2 (Stroje) — fleet dialog state (M2.2).
//
// Six dialogs, one owner. Split out of FleetV1Page so the page stays a
// composition (and under the 300-line cap) and so every open/close pair lives
// next to its sibling — the same dialog is opened from a card kebab AND from
// the toolbar, and split ownership is how a dialog ends up stuck open.
//
// The dialogs themselves are the EXISTING (MUI) ones, mounted by FleetDialogs
// per ticket §5 — this hook only tracks which is open and for which machine.
//
// `newMachineId` lives here because it is set by MachineDialog's onCreated
// callback; the page reads it for the NEW badge.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { useCallback, useState } from "react";

import { useAppDispatch } from "../../../../shared/hooks/sharedHooks";
import { Machine, MachinesQuery } from "../../models/models";
import {
  getDecommissionedMachines,
  getMachines,
  selectMachine,
} from "../../customerMachinerySlice";
import { fetchGroups } from "../../machineGroupsSlice";
import { FleetDialogsProps } from "./FleetDialogs";

export interface FleetDialogControls {
  /** Everything FleetDialogs needs except the slice-selected machine. */
  dialogProps: Omit<FleetDialogsProps, "selectedMachine">;
  newMachineId: string | null;
  setNewMachineId: (id: string | null) => void;
  openAdd: () => void;
  openManageGroups: () => void;
  openQr: (m: Machine) => void;
  openEdit: (m: Machine) => void;
  openAssignGroup: (m: Machine) => void;
  openDecommission: (m: Machine) => void;
}

export const useFleetDialogs = (query: MachinesQuery): FleetDialogControls => {
  const dispatch = useAppDispatch();
  const [newMachineId, setNewMachineId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [manageGroupsOpen, setManageGroupsOpen] = useState(false);
  const [assignGroupMachine, setAssignGroupMachine] = useState<Machine | null>(null);
  const [editMachine, setEditMachine] = useState<Machine | null>(null);
  const [decommissionMachine, setDecommissionMachine] = useState<Machine | null>(null);

  const openAdd = useCallback(() => {
    // MachineDialog reads the machine off the slice; null = create mode.
    dispatch(selectMachine(null));
    setAddOpen(true);
  }, [dispatch]);

  const openQr = useCallback(
    (m: Machine) => {
      dispatch(selectMachine(m));
      setQrOpen(true);
    },
    [dispatch]
  );

  const dialogProps: Omit<FleetDialogsProps, "selectedMachine"> = {
    addOpen,
    onAddClose: () => setAddOpen(false),
    onCreated: (id?: string) => {
      if (id) setNewMachineId(id);
    },
    qrOpen,
    onQrClose: () => setQrOpen(false),
    manageGroupsOpen,
    onManageGroupsClose: () => setManageGroupsOpen(false),
    assignGroupOpen: !!assignGroupMachine,
    assignGroupMachine,
    onAssignGroupClose: () => setAssignGroupMachine(null),
    onAssigned: () => {
      setAssignGroupMachine(null);
      dispatch(getMachines(query));
      // REVIEW F7 — the live handler (Machinery.tsx:779-782) also refreshes the
      // GROUPS. Without it ManageGroups keeps showing pre-assignment member
      // counts until the page is reloaded.
      dispatch(fetchGroups());
    },
    editMachine,
    onEditClose: () => setEditMachine(null),
    onEditSaved: () => setEditMachine(null),
    decommissionMachine,
    onDecommissionClose: () => setDecommissionMachine(null),
    onDecommissioned: () => {
      setDecommissionMachine(null);
      dispatch(getMachines(query));
      dispatch(getDecommissionedMachines());
    },
  };

  return {
    dialogProps,
    newMachineId,
    setNewMachineId,
    openAdd,
    openManageGroups: () => setManageGroupsOpen(true),
    openQr,
    openEdit: setEditMachine,
    openAssignGroup: setAssignGroupMachine,
    openDecommission: setDecommissionMachine,
  };
};

export default useFleetDialogs;
